import React, { createContext, useContext, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGuideProgress, GuideStep } from '@/hooks/useGuideProgress';

interface OnboardingContextType {
  steps: GuideStep[];
  completedSteps: string[];
  isLoading: boolean;
  isComplete: boolean;
  completedCount: number;
  totalCount: number;
  progressPercent: number;
  currentStep: GuideStep | null;
  currentStepIndex: number;
  nextStep: GuideStep | null;
  isOnboardingRoute: boolean;
  isCurrentStepCompleted: boolean;
  markStepComplete: (stepKey: string) => Promise<void>;
  goToNextStep: () => void;
  goToStep: (step: GuideStep) => void;
  refetch: () => Promise<void>;
}

const OnboardingContext = createContext<OnboardingContextType | null>(null);

// Map routes to step keys - Empty to hide progress bar on individual pages
// Progress is only shown on /getting-started page
const ROUTE_TO_STEP_MAP: Record<string, string> = {};

export function OnboardingProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const guideProgress = useGuideProgress();

  const {
    steps,
    completedSteps,
    isLoading,
    isComplete,
    completedCount,
    totalCount,
    progressPercent,
    markStepComplete,
    refetch,
  } = guideProgress;

  // Find the current step based on the route
  const currentStepKey = useMemo(() => {
    return ROUTE_TO_STEP_MAP[location.pathname] || null;
  }, [location.pathname]);

  const currentStep = useMemo(() => {
    if (!currentStepKey) return null;
    return steps.find(s => s.step_key === currentStepKey) || null;
  }, [currentStepKey, steps]);

  const currentStepIndex = useMemo(() => {
    if (!currentStep) return -1;
    return steps.findIndex(s => s.id === currentStep.id);
  }, [currentStep, steps]);

  const nextStep = useMemo(() => {
    if (currentStepIndex < 0 || currentStepIndex >= steps.length - 1) return null;
    return steps[currentStepIndex + 1];
  }, [currentStepIndex, steps]);

  const isOnboardingRoute = useMemo(() => {
    return currentStepKey !== null;
  }, [currentStepKey]);

  const isCurrentStepCompleted = useMemo(() => {
    if (!currentStep) return false;
    return completedSteps.includes(currentStep.step_key);
  }, [currentStep, completedSteps]);

  const goToNextStep = useCallback(() => {
    if (nextStep?.route) {
      navigate(nextStep.route);
    } else {
      // If no next step, go back to getting started
      navigate('/getting-started');
    }
  }, [nextStep, navigate]);

  const goToStep = useCallback((step: GuideStep) => {
    if (step.route) {
      navigate(step.route);
    }
  }, [navigate]);

  const value: OnboardingContextType = {
    steps,
    completedSteps,
    isLoading,
    isComplete,
    completedCount,
    totalCount,
    progressPercent,
    currentStep,
    currentStepIndex,
    nextStep,
    isOnboardingRoute,
    isCurrentStepCompleted,
    markStepComplete,
    goToNextStep,
    goToStep,
    refetch,
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding() {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within OnboardingProvider');
  }
  return context;
}

/**
 * Igual que `useOnboarding` pero devuelve `null` en vez de reventar cuando no
 * hay provider encima.
 *
 * El onboarding solo existe en el armazón de admin/manager (`MainLayout` monta
 * el `OnboardingProvider`). Los portales de perito y trabajador NO lo montan
 * —no tienen guía de puesta en marcha—, así que cualquier pieza COMPARTIDA por
 * los tres, como la ventana de perfil, tiene que poder preguntar por él sin
 * tumbar la aplicación entera.
 */
export function useOnboardingOptional() {
  return useContext(OnboardingContext);
}

// Export the route map for use in other components
export { ROUTE_TO_STEP_MAP };
